// src/components/CookAI/HowItWorks/HowItWorksSection.tsx
import React from "react";
import { Refrigerator, SlidersHorizontal, ChefHat } from "lucide-react";
import { ColorTheme } from "../../../themes/colors";
import { getCommonStyles } from "../../../themes/styles/common";
import Reveal from "../common/Reveal";
import { useLanguage } from "../../../i18n/LanguageContext";

interface HowItWorksSectionProps {
    theme: ColorTheme;
    isMobile: boolean;
}

const stepIcons = [
    <Refrigerator size={26} />,
    <SlidersHorizontal size={26} />,
    <ChefHat size={26} />,
];

const HowItWorksSection: React.FC<HowItWorksSectionProps> = ({ theme, isMobile }) => {
    const commonStyles = getCommonStyles(theme);
    const { t } = useLanguage();

    return (
        <section
            id="how-it-works"
            style={{
                padding: isMobile ? "4rem 1.25rem" : "6rem 2rem",
                background: theme.cardBackground,
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Faint oversized watermark */}
            <div aria-hidden style={{
                position: "absolute",
                top: "-40px",
                right: "-30px",
                fontSize: "22rem",
                fontWeight: 800,
                fontFamily: "'Bricolage Grotesque', sans-serif",
                color: `${theme.primary}0A`,
                lineHeight: 1,
                pointerEvents: "none",
                userSelect: "none",
            }}>
                1·2·3
            </div>

            <div style={{ maxWidth: "1180px", margin: "0 auto", position: "relative" }}>
                <Reveal>
                    <h2 style={commonStyles.sectionTitle}>{t.howItWorks.sectionTitle}</h2>
                    <p style={commonStyles.sectionSubtitle}>{t.howItWorks.sectionSubtitle}</p>
                </Reveal>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                        gap: isMobile ? "2.5rem" : "2rem",
                        position: "relative",
                    }}
                >
                    {/* Dashed connector line (desktop only) */}
                    {!isMobile && (
                        <div aria-hidden style={{
                            position: "absolute",
                            top: "44px",
                            left: "18%",
                            right: "18%",
                            borderTop: `2px dashed ${theme.primary}45`,
                            zIndex: 0,
                        }} />
                    )}

                    {t.howItWorks.steps.map((step, i) => (
                        <Reveal key={i} delay={isMobile ? 0 : i * 130}>
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                textAlign: "center",
                                position: "relative",
                                zIndex: 1,
                                padding: "0 0.5rem",
                            }}>
                                {/* Icon disc with step number */}
                                <div style={{ position: "relative", marginBottom: "1.5rem" }}>
                                    <div style={{
                                        width: "88px",
                                        height: "88px",
                                        borderRadius: "28px",
                                        background: i === 1
                                            ? `linear-gradient(135deg, ${theme.secondary} 0%, ${theme.primary} 100%)`
                                            : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "#FFFFFF",
                                        boxShadow: `0 16px 36px ${theme.primaryGlow}`,
                                        transform: `rotate(${i % 2 === 0 ? -3 : 3}deg)`,
                                    }}>
                                        {stepIcons[i]}
                                    </div>
                                    <div style={{
                                        position: "absolute",
                                        top: "-10px",
                                        right: "-10px",
                                        width: "30px",
                                        height: "30px",
                                        borderRadius: "50%",
                                        background: theme.textPrimary,
                                        color: "#FFFFFF",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.8rem",
                                        fontWeight: 800,
                                        fontFamily: "'Bricolage Grotesque', sans-serif",
                                        border: "2.5px solid #FFFFFF",
                                    }}>
                                        {i + 1}
                                    </div>
                                </div>

                                <h3 style={{
                                    fontSize: "1.25rem",
                                    fontWeight: 700,
                                    color: theme.textPrimary,
                                    marginBottom: "0.6rem",
                                    letterSpacing: "-0.01em",
                                }}>
                                    {step.title}
                                </h3>
                                <p style={{
                                    color: theme.textSecondary,
                                    lineHeight: 1.65,
                                    fontSize: "0.98rem",
                                    maxWidth: "300px",
                                }}>
                                    {step.description}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default HowItWorksSection;
